import React, { useEffect, useState } from 'react';
import ApiClient from '../../utils/ApiClient';
import { isAdmin } from '../../auth';

const FIELDS = [
  ['coverDays', 'Cover at landing (days)', null],
  ['supplierLeadDays', 'Schluter: PO to our dock (days)', 'supplierLeadDays'],
  ['handlingDays', 'Our handling: dock to Amazon shipment (days)', 'handlingDays'],
  ['amazonInboundDays', 'Amazon inbound: shipment to receiving (days)', 'amazonInboundDays'],
  // Owner 2026-11-03: nothing with a sales rank above this is ordered (0 = no ceiling).
  ['maxSalesRank', 'Do not order above sales rank (0 = no ceiling)', null],
];

const num = (v) => (typeof v === 'number' ? v : (String(v ?? '').trim() === '' ? NaN : Number(v)));

export default function ReorderSettingsModal({ open, onClose, onSaved }) {
  const [s, setS] = useState(null);
  const [measured, setMeasured] = useState({});
  const [meta, setMeta] = useState({});
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const canEdit = isAdmin();

  useEffect(() => {
    if (!open) return;
    setError(null);
    ApiClient.get('/api/po-creation/reorder-settings')
      .then(({ data }) => { setS(data.settings); setMeasured(data.measured || {}); setMeta({ by: data.updatedBy, at: data.updatedAt }); })
      .catch(() => setError('Could not load lead time settings'));
  }, [open]);

  if (!open) return null;
  const lead = s ? ['supplierLeadDays', 'handlingDays', 'amazonInboundDays'].reduce((a, k) => a + (Number(s[k]) || 0), 0) : 0;
  const cover = s ? Number(s.coverDays) || 0 : 0;

  const save = async () => {
    const out = {};
    const bad = [];
    FIELDS.forEach(([k, label]) => { const n = num(s[k]); if (!Number.isFinite(n)) bad.push(label); out[k] = n; });
    if (bad.length) { setError(`Enter a number for: ${bad.join(', ')}`); return; }
    setSaving(true);
    try {
      await ApiClient.put('/api/po-creation/reorder-settings', out);
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const measuredText = (key) => {
    const m = measured[key];
    if (!m) return null;
    return `measured ${m.median == null ? '—' : `${m.median} d`} (${m.n})`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-label="Lead time and cover">
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white p-5 text-gray-900 shadow-xl dark:bg-gray-800 dark:text-gray-100">
        <h2 className="mb-1 text-lg font-semibold">Lead time &amp; cover</h2>
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">Orders are sized so the cover days remain when the units are sellable at Amazon. Measured values are medians over the last 90 days (sample size in brackets).</p>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        {s && (
          <div className="space-y-3 text-sm">
            {FIELDS.map(([k, label, mKey]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <label htmlFor={`rs-${k}`}>{label}</label>
                <div className="flex items-center gap-2">
                  {mKey && <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{measuredText(mKey)}</span>}
                  <input id={`rs-${k}`} type="number" disabled={!canEdit} value={s[k] ?? ''}
                         onChange={e => setS({ ...s, [k]: e.target.value })}
                         className="w-20 rounded border px-1 py-0.5 dark:bg-gray-700" />
                </div>
              </div>
            ))}
            <p className="font-medium">Lead time: {lead} days · order covers {cover + lead} days</p>
            {meta.by && <p className="text-xs text-gray-500 dark:text-gray-400">Last changed by {meta.by} at {meta.at}</p>}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded border px-3 py-1">Close</button>
          {canEdit && s && (
            <button type="button" onClick={save} disabled={saving} className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50">Save</button>
          )}
        </div>
      </div>
    </div>
  );
}
